import { AlertTriangle, Archive, ArchiveRestore, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { SectionLabel } from "components/AppShell";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";

import {
  useEventArchive,
  type EventArchiveCommand,
  type EventArchiveState,
} from "./use-event-archive";

export interface ArchiveOutcome {
  command: EventArchiveCommand;
  superseded: boolean;
}

export const ARCHIVE_EXPLANATION =
  "Archived events leave the event list and stop sending mail and reminders. Participants can still read the event, and registration closes. You can restore it at any time.";
export const RESTORE_EXPLANATION =
  "The event returns to the event list, and mail, reminders and registration work again. Mail that was skipped while it was archived is not sent afterwards.";

/**
 * Whether a timed-out request shows as applied in the page's reload, which
 * runs once when the wait times out. Then the change went through after all
 * and there is nothing to try again.
 */
function appliedAfterTimeout(
  state: EventArchiveState,
  archived: boolean,
): boolean {
  return (
    state.status === "failed" &&
    state.timedOut !== undefined &&
    archived === (state.timedOut === "ARCHIVE_EVENT")
  );
}

function statusMessage(state: EventArchiveState, archived: boolean): string {
  if (appliedAfterTimeout(state, archived)) {
    return archived ? "Event archived." : "Event restored.";
  }
  switch (state.status) {
    case "submitting":
    case "watching":
      return state.command === "ARCHIVE_EVENT"
        ? "Archiving event…"
        : "Restoring event…";
    case "succeeded":
      // A superseded request did not change the event: a newer one decided
      // its state. Say that, whatever the page's read shows, because that
      // read can still be from before the reload and would flash the wrong
      // sentence. The button label follows the event once the reload lands.
      if (state.superseded)
        return "A newer request decided this event's state.";
      return state.command === "ARCHIVE_EVENT"
        ? "Event archived."
        : "Event restored.";
    default:
      return "";
  }
}

export function EventArchiveControl({
  eventId,
  eventTitle,
  uid,
  archived,
  onChanged,
  onSucceeded,
  onTimedOut,
  announceOutcome = true,
}: {
  eventId: string;
  eventTitle: string;
  uid: string;
  /** The event's archived state from the page's own read. */
  archived: boolean;
  /**
   * Called when a request settles, and once when the wait times out, so the
   * page reads the event again.
   */
  onChanged?: () => void;
  /**
   * Called once a request has gone through. A list that moves the event out
   * of view on reload confirms it above the rows instead, the way it
   * confirms a deletion.
   */
  onSucceeded?: (outcome: ArchiveOutcome) => void;
  /**
   * Called once when the wait for a request times out. The request can
   * still go through, and the reload that follows may show it, but a list
   * that moves the event on reload unmounts this control first, so the list
   * decides that case from its own read.
   */
  onTimedOut?: (command: EventArchiveCommand) => void;
  /**
   * False when the page announces the outcome itself (see onSucceeded): the
   * status line still announces progress, and the outcome sentence shows but
   * is hidden from screen readers, so they do not hear it twice.
   */
  announceOutcome?: boolean;
}) {
  const { state, pending, submit, reset } = useEventArchive(
    eventId,
    uid,
    onChanged,
  );
  const onSucceededRef = useRef(onSucceeded);
  const onTimedOutRef = useRef(onTimedOut);
  useEffect(() => {
    onSucceededRef.current = onSucceeded;
    onTimedOutRef.current = onTimedOut;
  });
  // Once per settled request: the state object changes only when the
  // request moves on.
  const reported = useRef<EventArchiveState | null>(null);
  useEffect(() => {
    if (reported.current === state) return;
    reported.current = state;
    if (state.status === "succeeded") {
      onSucceededRef.current?.({
        command: state.command,
        superseded: state.superseded,
      });
    } else if (state.status === "failed" && state.timedOut !== undefined) {
      onTimedOutRef.current?.(state.timedOut);
    }
  }, [state]);
  const message = statusMessage(state, archived);
  const [open, setOpen] = useState(false);
  const command = archived ? "RESTORE_EVENT" : "ARCHIVE_EVENT";
  const label = archived ? "Restore event" : "Archive event";
  const Icon = archived ? ArchiveRestore : Archive;

  return (
    <div className="space-y-3">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="min-h-11"
            disabled={pending}
            data-testid={`archive-event-${eventId}`}
          >
            <Icon className="mr-2 h-4 w-4" />
            {label}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {archived ? "Restore" : "Archive"} {eventTitle}?
            </DialogTitle>
            <DialogDescription>
              {archived ? RESTORE_EXPLANATION : ARCHIVE_EXPLANATION}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="cta"
              className="min-h-11"
              disabled={pending}
              data-testid="confirm-archive-event"
              onClick={() => {
                setOpen(false);
                void submit(command);
              }}
            >
              {label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p
        className="flex items-center gap-2 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
        data-testid="event-archive-status"
      >
        {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
        {announceOutcome || pending ? (
          message
        ) : (
          <span aria-hidden>{message}</span>
        )}
      </p>
      {state.status === "failed" && !appliedAfterTimeout(state, archived) && (
        <div
          className="flex flex-wrap items-start gap-2 text-sm text-destructive-text"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{state.error}</span>
          <Button
            variant="ghost"
            size="sm"
            className="min-h-11"
            onClick={reset}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

export function EventArchiveCard(props: {
  eventId: string;
  eventTitle: string;
  uid: string;
  archived: boolean;
  onChanged?: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 py-5">
        <SectionLabel>Archive</SectionLabel>
        <p className="text-sm text-muted-foreground">
          Take this event out of the event list and stop its mail, without
          deleting anything.
        </p>
        <EventArchiveControl {...props} />
      </CardContent>
    </Card>
  );
}
